import _ from "underscore";
import DriveView from "../../DriveView";
import Api from "../../services/api";
import toastr from "../../utils/toastr";
import Spinner from "../../utils/spinner";
import { NetworkStates, PageLevel } from "../../store/mobile/types";

import pageHTML from "./homeTerminalChange.tmpl";
import { handleApi } from "../../utils/error";
import { getUser, setUser } from "./helpers";

export default class ChangeHomeTerminalView extends DriveView<Backbone.Model> {

    protected strings = [
        "Home terminal successfully changed",
        "You cannot change your home terminal while offline."
    ];
    private readonly api: Api;
    private readonly currentUser: DeepReadonlyObject<IUser>;
    private readonly template = _.template(pageHTML);
    private readonly waiting = new Spinner();

    private readonly editHomeTerminalName = "edit-home-terminal-name-input";
    private readonly editHomeTerminalAddress = "edit-home-terminal-address-input";
    private readonly submitChangeButtonId = "#submitChange";
    private readonly sanitizedAddress: string;

    constructor(options) {
        super(options, {
            title: "Home terminal",
            activeSection: "settings",
            pageLevel: PageLevel.TWO
        });
        const canEditSettings = this.app.securityService.can("editUser");
        if (!canEditSettings) {
            // silently redirect to settings
            this.app.router.navigate("settings/main", true);
        }

        const user = this.app.userService.getCurrentUser();
        if (!user) {
            this.createErrorDialog("Failed to load current user");
            this.app.router.navigate("settings/main", true);
        }
        this.currentUser = user as DeepReadonlyObject<IUser>;
        this.api = this.app.api;

        this.sanitizedAddress = this.currentUser.companyAddress ? this.replaceLineBreaksWithCommas(this.currentUser?.companyAddress) : "";
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #submitChange": this.submitChange
        } as Backbone.EventsHash;
    }

    render() {
        void this.display(this.template({ sanitizedAddress: this.sanitizedAddress })).then(() => {
            const homeTerminalNameInputEl = (document.getElementById(this.editHomeTerminalName) as HTMLInputElement);
            if (homeTerminalNameInputEl) {
                homeTerminalNameInputEl.value = this.currentUser?.companyName ?? "";
            }
        });
        return this;
    }

    onDone() {
        this.stopProgress();
        toastr.success(this.getString(this.strings[0]));
        this.app.router.navigate("settings/main", true);
    }

    createErrorDialog(errorMessage) {
        toastr.error(
            this.getString(errorMessage),
            undefined,
            undefined,
            {
                isUserNightMode: this.app.store.getState().data.nightMode,
                toastId: "home_terminal_change_error",
                origin: "settings_home_terminal_changene"
            }
        );
    }

    private readonly replaceLineBreaksWithCommas = (input: string) => input.replace(/(\r\n|\n|\r)(,\s*)?/g, ", ");

    private startProgress() {
        let submitButton = <HTMLButtonElement> this.el.querySelector(this.submitChangeButtonId);
        submitButton && submitButton.setAttribute("disabled", "disabled");
        submitButton && this.waiting.spin(submitButton);
    }

    private stopProgress() {
        let submitChangeButton = <HTMLButtonElement> this.el.querySelector(this.submitChangeButtonId);
        submitChangeButton && submitChangeButton.removeAttribute("disabled");
        submitChangeButton && this.waiting.stop();
    }

    private submitChange() {
        this.startProgress();

        if (this.app.store.getState().mobile.online !== NetworkStates.ONLINE) {
            this.createErrorDialog(this.getString(this.strings[1]));
            this.stopProgress();
            return;
        }

        const companyName: string = this.el.querySelector(`#${ this.editHomeTerminalName }`).value;
        const companyAddress: string = this.el.querySelector(`#${ this.editHomeTerminalAddress }`).value;

        getUser(this.api, this.currentUser.id).then(user => {
            const updatedUser = {
                ...user,
                companyName,
                companyAddress
            };
            setUser(this.api, updatedUser).then(() => {
                this.app.userService.setUser(updatedUser);
                this.onDone();
            }).catch(error => {
                this.stopProgress();
                if (error) {
                    this.createErrorDialog(error.message);
                    handleApi(error);
                }
            });
        }).catch(error => {
            this.stopProgress();
            if (error) {
                this.createErrorDialog("We could not retrieve user information, please try again.");
                handleApi(error);
            }
        });
    }
}